import { MouseEventHandler, useCallback, useMemo } from "react"

import { getColumnId } from "./getColumnId"
import { getVisibleColumns } from "./getVisibleColumns"
import {
  ColumnType,
  DataColumn,
  GroupColumn,
  RowReorderingMode,
  UseTableBodyProps,
} from "./types"
import { useUrlQuery } from "../../../../hooks/useUrlQuery"
import { openTab } from "../../../../lib/openTab"
import { BodyCellType, BodyRowCell, TableBody } from "../Table"

export function useTableBody<T>({
  data,
  columns,
  extractRowKey,
  rowSelectionKind,
  state,
  dispatch,
  onClickRow,
  extractRowHref,
  enableRowHighlighting,
  hiddenColumns,
  emptyPlaceholderMessage,
  rowReorderingMode = RowReorderingMode.Hidden,
  rowReorderingDisabledMessage,
  hideColumnBorders,
}: UseTableBodyProps<T>): TableBody {
  const { push } = useUrlQuery()
  const mapToBodyCells = useCallback<
    (group: GroupColumn<T>, rowData: T, rowKey: string) => BodyRowCell[]
  >(
    (group, rowData) =>
      getVisibleColumns(group, state.expandedGroups, hiddenColumns).map(
        (subCol) => mapToBodyCell(subCol, rowData, group)
      ),
    [state.expandedGroups, hiddenColumns]
  )

  return useMemo<TableBody>(() => {
    const rows = data.map((rowData) => {
      const rowKey = extractRowKey(rowData)
      const cells: BodyRowCell[] = columns.flatMap((col) => {
        if (col.type === ColumnType.Group) {
          return mapToBodyCells(col, rowData, rowKey)
        }
        return hiddenColumns?.has(getColumnId(col))
          ? []
          : [mapToBodyCell(col, rowData)]
      })
      if (cells.length > 0) {
        if (rowSelectionKind === "single-select") {
          cells.unshift({
            key: [rowKey, "radio"].join("-"),
            type: BodyCellType.RadioButton,
            isChecked: state.selectedByKey.has(rowKey),
            onChangeIsChecked: () => {
              dispatch({
                type: "toggle_row_selected",
                rowData,
                visibleRowData: data,
              })
            },
          })
        } else if (rowSelectionKind === "multi-select") {
          cells.unshift({
            key: [rowKey, "checkbox"].join("-"),
            type: BodyCellType.Checkbox,
            isChecked: state.isAllSelected || state.selectedByKey.has(rowKey),
            onChangeIsChecked: ({ isChecked: _, isShiftPressed }) => {
              if (isShiftPressed) {
                dispatch({
                  type: "toggle_row_selected_with_shift",
                  rowData,
                  visibleRowData: data,
                })
                return
              }
              dispatch({
                type: "toggle_row_selected",
                rowData,
                visibleRowData: data,
              })
            },
          })
        }

        if (rowReorderingMode !== RowReorderingMode.Hidden) {
          cells.unshift({
            key: [rowKey, "drag-handle"].join("-"),
            type: BodyCellType.DragHandle,
            isDisabled: rowReorderingMode === RowReorderingMode.Disabled,
            disabledMessage: rowReorderingDisabledMessage,
          })
        }
      }

      const onClick: MouseEventHandler<HTMLTableRowElement> | undefined =
        (onClickRow || extractRowHref) &&
        ((e) => {
          if (onClickRow) {
            onClickRow(rowData, e)
            return
          }
          const href = extractRowHref?.(rowData)
          if (!href) {
            return
          }
          if (e.ctrlKey || e.metaKey) {
            openTab(href)
          } else {
            push(href)
          }
        })

      return {
        key: rowKey,
        cells,
        onClick,
      }
    })
    return {
      rows,
      enableRowHighlighting,
      emptyPlaceholderMessage,
      hideColumnBorders,
    }
  }, [
    data,
    enableRowHighlighting,
    emptyPlaceholderMessage,
    extractRowKey,
    columns,
    onClickRow,
    extractRowHref,
    push,
    hiddenColumns,
    mapToBodyCells,
    rowSelectionKind,
    state.selectedByKey,
    state.isAllSelected,
    dispatch,
    rowReorderingMode,
    rowReorderingDisabledMessage,
    hideColumnBorders,
  ])
}

function mapToBodyCell<T>(
  col: DataColumn<T>,
  rowData: T,
  group?: GroupColumn<T>
): BodyRowCell {
  return {
    key: [group?.title, col.title].filter(Boolean).join("-"),
    type: BodyCellType.Data,
    content: col.renderCell(rowData),
    testId: col.extractCellTestId?.(rowData),
  }
}
